/** @format */

import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import {
	Inject,
	Injectable,
	PLATFORM_ID,
	StateKey,
	TransferState,
	makeStateKey
} from '@angular/core';

import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { RatingSummaryResponse, StatisticsResponse } from '../model/review.summary.interface';
import { ReviewRequest } from '../model/review.interface';
import {
	BVSummarisedFeaturesResponse,
	FeedbackResponse,
	FeedbackType,
	FilterType,
	Helpful,
	ProductReviewsProsCons,
	ReviewDaoRequestKey
} from '../model';
import { CustomHttpHeaders, MediaType } from '@shared/data-access/enums';
import { SelectedFilterType } from 'libs/product-reviews/components/src/lib/models/filter.interface';
import { Logger } from '@shared/data-access/services';
import { ProductReviewAnalyticsService } from '../service/product-review.analytics.service';

import { Chain, Environment, ReviewAISummaryResponse } from '@shared/data-access/interfaces';
@Injectable({
	providedIn: 'root'
})
export class ReviewDao {
	readonly REVIEW_SUMMARY_BY_PRODUCT_STATE_KEY_PREFIX = 'product-reviews-';
	readonly REVIEW_STATISTICS_BY_PRODUCT_STATE_KEY_PREFIX = 'product-reviews-statistics';
	readonly LATEST_REVIEW_STATE_KEY_PREFIX = 'review-latest-';
	readonly MULTIMEDIA_REVIEWS_KEY_PREFIX = 'multimedia-review';
	readonly REVIEWS_STATE_KEY_PREFIX = 'reviews-list-';
	readonly REVIEW_AI_SUMMARY_BY_PRODUCT_STATE_KEY_PREFIX = 'review-ai-summary';
	readonly REVIEW_PROS_CONS_BY_PRODUCT_STATE_KEY_PREFIX = 'review-pros-cons';

	constructor(
		private chain: Chain,
		private environment: Environment,
		private http: HttpClient,
		@Inject(PLATFORM_ID) protected platformId: string,
		private state: TransferState,
		private logger: Logger,
		private analyticsService: ProductReviewAnalyticsService
	) {}

	/**
	 * @description
	 * Gets review data from BazaarVoice for a given product by ID
	 */
	getReviewsByProductId$(
		productId: string,
		options: ReviewRequest = { sortBy: 'SubmissionTime:desc', limitTo: 15 }
	): Observable<RatingSummaryResponse | any> {
		const { api: BVApi } = this.environment.thirdParty.bv;
		const reviewsByID = `${BVApi.proxyConfig.proxy.path}${BVApi.reviewsUrl}`;
		//Get unique key
		const STATE_KEY = makeStateKey<RatingSummaryResponse>(
			`${this.REVIEW_SUMMARY_BY_PRODUCT_STATE_KEY_PREFIX}${productId.toUpperCase()}`
		);

		// Attempt to get the state by key
		const stateData: any = this.state.get(STATE_KEY, null);
		// Check to see if state exists
		if (stateData) {
			// Return the existing stateviewsByProductId has state data ');
			return of(stateData);
		}

		// Query String Parameters
		const httpParams = new HttpParams()
			.set(
				'passkey',
				`${
					this.environment.thirdParty.bv.api[this.chain.chainIdentifierAbbr]
						.reviewSummaryPassKey
				}`
			)
			.set('apiVersion', `${BVApi.revewSummaryApiVersion}`)
			.set('filter', `productId:${productId}`)
			.set('Include', 'Products')
			.set('Stats', 'Reviews')
			.set('TagStats', true)
			.set('Sort', options.sortBy)
			.set('Limit', options.limitTo);

		// Standard HTTP Headers
		let httpHeaders = new HttpHeaders({
			Accept: MediaType.APPLICATION_JSON
		});

		// Check platform
		if (!isPlatformBrowser(this.platformId)) {
			// Set a timeout on the server to prevent the response from taking too long
			httpHeaders = httpHeaders
				.set(CustomHttpHeaders.X_TIMEOUT, `${DCSG_CONSTANTS.timeouts.short}`)
				.set(CustomHttpHeaders.X_PROXY_REQUEST, 'true');
		}

		// Make the HTTP Request
		return this.http
			.get<RatingSummaryResponse>(reviewsByID, {
				headers: httpHeaders,
				params: httpParams
			})
			.pipe(
				tap((response: RatingSummaryResponse) => {
					// Set the state
					this.state.set<RatingSummaryResponse>(STATE_KEY, response);
					if (response.HasErrors) {
						this.logger.dynamicallyLog(
							ReviewDaoRequestKey.BYPRODUCTID,
							productId,
							response.Errors,
							reviewsByID
						);
						this.analyticsService.fireReviewAnalytics<{ event: string; value: string }>(
							{
								name: 'SiteError',
								payload: {
									event: 'SiteError',
									value: `[${ReviewDaoRequestKey.BYPRODUCTID}] API endpoint call ${productId} failed ${reviewsByID}.`
								}
							}
						);
					}
				}),
				catchError((error: HttpErrorResponse) => {
					this.logger.dynamicallyLog(
						ReviewDaoRequestKey.BYPRODUCTID,
						productId,
						error,
						reviewsByID
					);
					return throwError(() => error);
				})
			);
	}

	/**
	 * @description
	 * Gets the AI Summary for all reviews from BazaarVoice for a given product by ID
	 * @param productID - product ID to query
	 * @return HTTPClient Get Observable
	 */
	public getReviewAISummaryByProductId$(productId: string): Observable<ReviewAISummaryResponse> {
		// Get unique key
		const STATE_KEY: StateKey<ReviewAISummaryResponse> = makeStateKey(
			`${this.REVIEW_AI_SUMMARY_BY_PRODUCT_STATE_KEY_PREFIX}${productId.toUpperCase()}`
		);

		// Attempt to get the state by key
		const stateData = this.state.get(STATE_KEY, null);

		// Check to see if state exists
		if (stateData) {
			// Return the existing state
			return of(stateData);
		}

		// Query String Parameters
		const httpParams = new HttpParams()
			.set('productId', productId)
			.set(
				'passkey',
				`${
					this.environment.thirdParty.bv.api[this.chain.chainIdentifierAbbr]
						.reviewSummaryPassKey
				}`
			);

		// Standard HTTP Headers
		let httpHeaders = new HttpHeaders({
			Accept: MediaType.APPLICATION_JSON
		});

		// Check platform
		if (!isPlatformBrowser(this.platformId)) {
			// Set a timeout on the server to prevent the response from taking too long
			httpHeaders = httpHeaders.set(
				CustomHttpHeaders.X_TIMEOUT,
				`${DCSG_CONSTANTS.timeouts.short}`
			);
			httpHeaders = httpHeaders.set(CustomHttpHeaders.X_PROXY_REQUEST, 'true');
		}

		// HTTP Options
		const httpOptions = {
			headers: httpHeaders,
			params: httpParams
		};

		const { api: BVApi } = this.environment.thirdParty.bv;

		const url = `${BVApi.proxyConfig.proxy.path}${BVApi.reviewAISummaryUrl}`;

		// Make the HTTP Request
		return this.http.get(url, httpOptions).pipe(
			map((response: ReviewAISummaryResponse) => {
				// Set the state
				this.state.set(STATE_KEY, response);

				// Return the response
				return response;
			})
		);
	}

	addParameter(params: HttpParams, name: string, filter: string | string[]) {
		if (filter && Array.isArray(filter)) {
			filter.forEach((element) => {
				params = params.append('filter', element);
			});
		} else if (typeof filter === 'string') {
			params = params.append(name, filter);
		}
		return params;
	}

	/**
	 * @description
	 * Gets statistics data from BazaarVoice for a given product by ID
	 */
	getStatisticsByProductId$(productId: string): Observable<StatisticsResponse> {
		const { api: BVApi } = this.environment.thirdParty.bv;
		const statisticsURL = `${BVApi.proxyConfig.proxy.path}${BVApi.statisticsUrl}`;
		// Get unique key
		const STATE_KEY = makeStateKey<StatisticsResponse>(
			`${this.REVIEW_STATISTICS_BY_PRODUCT_STATE_KEY_PREFIX}${productId}`
		);

		// Attempt to get the state by key
		const stateData: any = this.state.get(STATE_KEY, null);

		// Check to see if state exists
		if (stateData) {
			// Return the existing state
			return new Observable((observer) => {
				observer.next(stateData);
				observer.complete();
			});
		}

		// Query String Parameters
		const httpParams = new HttpParams()
			.set('passkey', `${BVApi[this.chain.chainIdentifierAbbr].reviewSummaryPassKey}`)
			.set('apiVersion', `${BVApi.revewSummaryApiVersion}`)
			.set('filter', `productId:${productId}`)
			.set('stats', 'Questions,Answers');

		// Standard HTTP Headers
		let httpHeaders = new HttpHeaders({
			Accept: MediaType.APPLICATION_JSON
		});

		// Check platform
		if (!isPlatformBrowser(this.platformId)) {
			// Set a timeout on the server to prevent the response from taking too long
			httpHeaders = httpHeaders.set(
				CustomHttpHeaders.X_TIMEOUT,
				`${DCSG_CONSTANTS.timeouts.short}`
			);
			httpHeaders = httpHeaders.set(CustomHttpHeaders.X_PROXY_REQUEST, 'true');
		}

		// Make the HTTP Request
		return this.http
			.get(statisticsURL, {
				headers: httpHeaders,
				params: httpParams
			})
			.pipe(
				map((response: StatisticsResponse) => {
					// Set the state
					this.state.set<StatisticsResponse>(STATE_KEY, response);
					if (response.HasErrors) {
						this.logger.dynamicallyLog(
							ReviewDaoRequestKey.STATISTICS,
							productId,
							response.Errors,
							statisticsURL
						);
						this.analyticsService.fireReviewAnalytics<{ event: string; value: string }>(
							{
								name: 'SiteError',
								payload: {
									event: 'SiteError',
									value: `[${ReviewDaoRequestKey.STATISTICS}] API endpoint call ${productId} failed ${statisticsURL}.`
								}
							}
						);
					}
					// Return the response
					return response;
				}),
				catchError((error: HttpErrorResponse) => {
					this.logger.dynamicallyLog(
						ReviewDaoRequestKey.STATISTICS,
						productId,
						error,
						statisticsURL
					);
					return throwError(() => error);
				})
			);
	}

	getReviewsMultimedia$(productId: string): Observable<RatingSummaryResponse> {
		const { api: BVApi } = this.environment.thirdParty.bv;
		const reviewsMultimedia = `${BVApi.proxyConfig.proxy.path}${BVApi.reviewsUrl}`;
		// Get unique key
		const STATE_KEY = makeStateKey<RatingSummaryResponse | null>(
			`${this.MULTIMEDIA_REVIEWS_KEY_PREFIX}${productId}`
		);

		// Attempt to get the state by key
		const stateData: RatingSummaryResponse | null = this.state.get(STATE_KEY, null);

		// Check to see if state exists
		if (stateData) {
			// Return the existing state
			return of(stateData);
		}

		// Query String Parameters
		const httpParams = new HttpParams()
			.set('passkey', `${BVApi[this.chain.chainIdentifierAbbr].reviewSummaryPassKey}`)
			.set('apiVersion', `${BVApi.revewSummaryApiVersion}`)
			.set('Limit', 20)
			.set('filter', `productId:${productId}`)
			.append('filter', 'HasMedia:true')
			.set('Sort', 'SubmissionTime:desc');

		// Standard HTTP Headers
		let httpHeaders = new HttpHeaders({
			Accept: MediaType.APPLICATION_JSON
		});

		// Check platform
		if (!isPlatformBrowser(this.platformId)) {
			// Set a timeout on the server to prevent the response from taking too long
			httpHeaders = httpHeaders.set(
				CustomHttpHeaders.X_TIMEOUT,
				`${DCSG_CONSTANTS.timeouts.short}`
			);
			httpHeaders = httpHeaders.set(CustomHttpHeaders.X_PROXY_REQUEST, 'true');
		}

		// Make the HTTP Request
		return this.http
			.get<RatingSummaryResponse>(reviewsMultimedia, {
				headers: httpHeaders,
				params: httpParams
			})
			.pipe(
				tap((response: RatingSummaryResponse) => {
					// Set the state
					this.state.set<RatingSummaryResponse>(STATE_KEY, response);
					if (response.HasErrors) {
						this.logger.dynamicallyLog(
							ReviewDaoRequestKey.MULTIMEDIA,
							productId,
							response.Errors,
							reviewsMultimedia
						);
						this.analyticsService.fireReviewAnalytics<{ event: string; value: string }>(
							{
								name: 'SiteError',
								payload: {
									event: 'SiteError',
									value: `[${ReviewDaoRequestKey.MULTIMEDIA}] API endpoint call ${productId} failed ${reviewsMultimedia}.`
								}
							}
						);
					}
				}),
				catchError((error: HttpErrorResponse) => {
					this.logger.dynamicallyLog(
						ReviewDaoRequestKey.MULTIMEDIA,
						productId,
						error,
						reviewsMultimedia
					);
					return throwError(() => error);
				})
			);
	}

	getListReviews$(
		productId: string,
		options: ReviewRequest = { offset: 0, limitTo: 15 }
	): Observable<RatingSummaryResponse> {
		const { chainIdentifierAbbr: key } = this.chain;
		const { api: BVApi } = this.environment.thirdParty.bv;
		const sortByParams = options.sortBy ? options.sortBy : 'SubmissionTime:desc';
		const reviewsWithFilter = `${BVApi.proxyConfig.proxy.path}${BVApi.reviewsUrl}`;

		// Query String Parameters
		let httpParams = new HttpParams()
			.set('passkey', `${BVApi[key].reviewSummaryPassKey}`)
			.set('apiVersion', `${BVApi.revewSummaryApiVersion}`)
			.set('Filter', `productId:${productId}`)
			.set('Limit', options.limitTo)
			.set('Offset', options.offset)
			.set('Sort', sortByParams);

		if (options.filter.length) {
			const ratingValues: string[] = [];
			options.filter.forEach(({ id, name, value, type }) => {
				if (type === FilterType.TAG) {
					httpParams = this.addParameter(httpParams, 'filter', `tag_${name}:eq:${value}`);
				} else if (type === FilterType.RATING) {
					ratingValues.push(value as string);
				} else if (
					type === FilterType.STANDARD &&
					id === SelectedFilterType.VERIFIED_PURCHASER
				) {
					httpParams = this.addParameter(
						httpParams,
						'filter',
						`ContextDataValue_${name}:${value}`
					);
				} else {
					httpParams = this.addParameter(httpParams, 'filter', `${name}:${value}`);
				}
			});

			if (ratingValues.length) {
				const payload = `${FilterType.RATING}:eq:${ratingValues.join(',')}`;
				httpParams = this.addParameter(httpParams, 'filter', payload);
			}
		}

		if (options.search) {
			httpParams = httpParams.set('Search', options.search);
		}

		if (options.feature) {
			httpParams = httpParams.set('feature', options.feature);
		}

		// Standard HTTP Headers
		let httpHeaders = new HttpHeaders({ Accept: MediaType.APPLICATION_JSON });

		// Check platform
		if (!isPlatformBrowser(this.platformId)) {
			// Set a timeout on the server to prevent the response from taking too long
			httpHeaders = httpHeaders.set(
				CustomHttpHeaders.X_TIMEOUT,
				DCSG_CONSTANTS.timeouts.short
			);
		}

		// Make the HTTP Request
		return this.http
			.get<RatingSummaryResponse>(reviewsWithFilter, {
				headers: httpHeaders,
				params: httpParams
			})
			.pipe(
				tap((response: RatingSummaryResponse) => {
					if (response.HasErrors) {
						this.logger.dynamicallyLog(
							ReviewDaoRequestKey.LIST,
							productId,
							response.Errors,
							reviewsWithFilter
						);
						this.analyticsService.fireReviewAnalytics<{ event: string; value: string }>(
							{
								name: 'SiteError',
								payload: {
									event: 'SiteError',
									value: `[${ReviewDaoRequestKey.LIST}] API endpoint call ${productId} failed ${reviewsWithFilter}.`
								}
							}
						);
					}
				}),
				catchError((error: HttpErrorResponse) => {
					this.logger.dynamicallyLog(
						ReviewDaoRequestKey.LIST,
						productId,
						error,
						reviewsWithFilter
					);
					return throwError(() => error);
				})
			);
	}

	/**
	 * @description
	 * Service Post for submitfeedback for helpful Inappropriate and Helpfulness
	 */
	setSubmitFeedback$(helpfulBody: Helpful): Observable<FeedbackResponse> {
		const { api: BVApi } = this.environment.thirdParty.bv;
		const feedbackURL = `${BVApi.proxyConfig.proxy.path}${BVApi.feedbackUrl}`;
		// // Standard HTTP Headers
		const headers = new HttpHeaders().set(
			'Content-Type',
			MediaType.APPLICATION_WWW_FORM_URLENCODED
		);

		// Objet QueryParamsFeedback
		let params = new HttpParams()
			.set('passkey', BVApi[this.chain.chainIdentifierAbbr].reviewSummaryPassKey)
			.set('apiVersion', `${BVApi.revewSummaryApiVersion}`)
			.set('contentType', 'review')
			.set('contentId', helpfulBody.contentId)
			.set('feedbackType', helpfulBody.feedbackType);

		if (helpfulBody.feedbackType === FeedbackType.HELPFULNESS) {
			params = params.set('vote', helpfulBody.vote);
		}

		// Make the HTTP Request
		return this.http.post<FeedbackResponse>(feedbackURL, null, { headers, params }).pipe(
			tap((response: FeedbackResponse) => {
				if (response.HasErrors) {
					this.logger.dynamicallyLog(
						ReviewDaoRequestKey.SUBMITFEEDBACK,
						'',
						response.Errors,
						feedbackURL
					);
					this.analyticsService.fireReviewAnalytics<{ event: string; value: string }>({
						name: 'SiteError',
						payload: {
							event: 'SiteError',
							value: `[${ReviewDaoRequestKey.SUBMITFEEDBACK}] API endpoint failed ${feedbackURL}.`
						}
					});
				}
			}),
			catchError((error: HttpErrorResponse) => {
				this.logger.dynamicallyLog(
					ReviewDaoRequestKey.SUBMITFEEDBACK,
					'',
					error,
					feedbackURL
				);
				return throwError(() => error);
			})
		);
	}

	/**
	 * @description
	 * Get the Pros and Cons of a product by ID
	 * @param productId - product ID to query
	 * @return HTTPClient Get Observable
	 */
	public getProsConsByProductId$(productId: string): Observable<ProductReviewsProsCons> {
		// Query String Parameters
		const httpParams = new HttpParams()
			.set('productId', productId)
			.set('language', 'en')
			.set('embed', 'quotes');

		// Standard HTTP Headers
		let httpHeaders = new HttpHeaders({
			Accept: MediaType.APPLICATION_JSON,
			Authorization: `${
				this.environment.thirdParty.bv.api[this.chain.chainIdentifierAbbr]
					.sentimentsApiAuthHeaderKey
			}`.trim()
		});
		httpHeaders = httpHeaders.set(CustomHttpHeaders.X_PROXY_REQUEST, 'true');

		// HTTP Options
		const httpOptions = {
			headers: httpHeaders,
			params: httpParams
		};

		const { api: BVApi } = this.environment.thirdParty.bv;

		const url = `${BVApi.proxyConfig.proxy.path}${BVApi.summarisedFeaturesUrl}`;

		// Make the HTTP Request
		return this.http.get(url, httpOptions).pipe(
			// Map the response to the Pros and Cons
			map((response: BVSummarisedFeaturesResponse) => ({
				cons: Array.isArray(response?.worstFeatures) ? response.worstFeatures : [],
				pros: Array.isArray(response?.bestFeatures) ? response.bestFeatures : []
			}))
		);
	}
}
